// La tabla compartida de observaciones postventa (handoff listado §3):
// la usan el listado del proyecto y el archivo, con columnas
// configurables. Presentacional y controlada — el filtrado, la
// paginación y la selección viven en quien la monta
// (lib/postventa/observacionesList.ts).
//
// Fila clicable sin romper la semántica (decisión 9 del fundador,
// 2026-10-03): el título es el ÚNICO <a href> de la fila y un
// pseudo-elemento lo estira sobre el resto de la fila («enlace
// estirado», ver .row-title::after en dashboard.css). Nunca onClick en
// <tr> ni role=link. La casilla de selección queda por encima (z-index)
// para que marcarla no abra la observación.
import type { ReactNode, RefObject } from "react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ESTADO_LABEL } from "@bitacorapro/api-types";
import { Icon } from "./icons";
import type { FilaObs } from "../lib/postventa/observacionesList";

export type ObsColumn = {
  key: string;
  header: string;
  cell: (f: FilaObs) => ReactNode;
  className?: string;
};

export type ObsSelection = {
  selected: ReadonlySet<string>;
  isSelectable: (f: FilaObs) => boolean;
  onToggle: (id: string) => void;
  /** Marca (true) o desmarca (false) estas filas de la página visible. */
  onToggleVisible: (ids: string[], select: boolean) => void;
  /** Tope del lote: con la selección llena, las no marcadas se
   *  deshabilitan. */
  max: number;
  masterRef: RefObject<HTMLInputElement>;
};

export const OBS_SEL_NOTA_ID = "obs-sel-nota";
const OBS_SEL_ESTADO_ID = "obs-sel-estado";

export function ObservacionesTable({
  filas,
  columns,
  rowHref,
  caption,
  selection,
  wide = false,
}: {
  /** Las filas de la página visible, ya filtradas y ordenadas. */
  filas: readonly FilaObs[];
  /** Columnas después de «Observación» (que la tabla pinta siempre). */
  columns: readonly ObsColumn[];
  rowHref: (f: FilaObs) => string;
  caption: string;
  selection?: ObsSelection;
  /** Tabla ancha (min-width 1180px) con las dos primeras columnas fijas
   *  al desplazar en horizontal. */
  wide?: boolean;
}) {
  const visibles = selection ? filas.filter(selection.isSelectable).map((f) => f.o.id) : [];
  const marcadas = selection ? visibles.filter((id) => selection.selected.has(id)) : [];
  const todas = visibles.length > 0 && marcadas.length === visibles.length;
  const lleno = selection ? selection.selected.size >= selection.max : false;

  const masterRef = selection?.masterRef;
  const parcial = marcadas.length > 0 && !todas;
  useEffect(() => {
    if (masterRef?.current) masterRef.current.indeterminate = parcial;
  });

  const tableClass = [
    "dtable",
    "dtable--compact",
    "dtable--rowlink",
    wide ? "dtable--obs dtable--sticky-cols" : "",
    selection ? "" : "dtable--no-check",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        className="dtable-scroll dtable-scroll--obs"
        tabIndex={0}
        role="region"
        aria-label="Tabla de observaciones"
      >
        <table className={tableClass}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {selection && (
                <th scope="col" className="col-check">
                  <span className="sr-only">Seleccionar</span>
                  <input
                    ref={selection.masterRef}
                    type="checkbox"
                    checked={todas}
                    disabled={visibles.length === 0 || (lleno && marcadas.length === 0)}
                    onChange={() => selection.onToggleVisible(visibles, !todas)}
                    aria-label="Seleccionar todas las observaciones asignables de esta página"
                    aria-describedby={`${OBS_SEL_NOTA_ID} ${OBS_SEL_ESTADO_ID}`}
                  />
                  <span id={OBS_SEL_ESTADO_ID} className="sr-only">
                    {marcadas.length} de {visibles.length} seleccionadas
                  </span>
                </th>
              )}
              <th scope="col" className="col-title">
                Observación
              </th>
              {columns.map((c) => (
                <th key={c.key} scope="col" className={c.className}>
                  {c.header}
                </th>
              ))}
              <th scope="col" className="col-chev">
                <span className="sr-only">Abrir</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => {
              const id = f.o.id;
              const checked = selection?.selected.has(id) ?? false;
              const selectable = selection?.isSelectable(f) ?? false;
              return (
                <tr key={id} className={checked ? "is-selected" : undefined}>
                  {selection && (
                    <td className="col-check">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!selectable || (!checked && lleno)}
                        onChange={() => selection.onToggle(id)}
                        aria-label={`Seleccionar: ${f.titulo}`}
                      />
                    </td>
                  )}
                  <td className="col-title">
                    <div className="cell-with-sub">
                      <Link
                        to={rowHref(f)}
                        className="cell-strong cell-strong--link row-title"
                        title={f.titulo}
                      >
                        {f.titulo}
                      </Link>
                      <span className="cell-sub">{f.recinto}</span>
                    </div>
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className={c.className}>
                      {c.cell(f)}
                    </td>
                  ))}
                  <td className="cell-meta col-chev" aria-hidden="true">
                    <Icon.ChevronRight s={16} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selection && (
        <p id={OBS_SEL_NOTA_ID} className="cell-sub obs-sel-nota">
          Solo las observaciones {ESTADO_LABEL.accepted} o {ESTADO_LABEL.open} se pueden asignar.
          Máximo {selection.max} por lote.
        </p>
      )}
    </>
  );
}
